import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  useGetTasksLazyQuery,
  NoteFilter,
  NoteTypeEnum,
  NoteStatusEnum,
  NotePriorityEnum,
  SortInput,
  Note
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';
import { tasks as dummyTasks } from '@/data/dummy';

interface UseTasksOptions {
  filter?: Partial<NoteFilter>;
  sort?: SortInput[];
  pageSize?: number;
  /** When true, skip the network fetch (e.g. a closed sheet that still mounts this hook). */
  skip?: boolean;
}

/**
 * Module-level defaults. These MUST NOT be inline default parameter values:
 * a default `{}` / `[...]` is re-evaluated on every render, so it would change
 * the fetch effect's dependencies every render and refetch forever (the effect
 * writes data -> re-render -> fresh reference -> effect runs again). Callers
 * that omitted `sort` drove exactly that loop from the Person and Deal detail
 * sheets, at one request per round-trip for as long as the sheet stayed open.
 */
const DEFAULT_FILTER: Partial<NoteFilter> = {};
const DEFAULT_SORT: SortInput[] = [{ field: 'createdAt', sort: 'DESC' }];

/**
 * Hook for fetching and managing tasks
 * Supports server-side filtering, sorting, and pagination
 *
 * @example
 * ```typescript
 * const { tasks, loading, hasMore, loadMore, refetch } = useTasks({
 *   filter: { status: NoteStatusEnum.TODO },
 *   sort: [{ field: 'properties.due_date', sort: 'ASC' }],
 *   pageSize: 50,
 * });
 * ```
 */
export const useTasks = ({
  filter: externalFilter = DEFAULT_FILTER,
  sort = DEFAULT_SORT,
  pageSize = 100,
  skip = false,
}: UseTasksOptions = {}) => {
  const workspaceId = useWorkspaceId();
  const [hasMore, setHasMore] = useState(false);
  const previousCountRef = useRef(0);

  const [getTasks, { data, loading, error, fetchMore, refetch }] =
    useGetTasksLazyQuery({ fetchPolicy: 'cache-and-network' });

  // Build filter - combine base filters with external filters
  const filter: NoteFilter = useMemo(() => ({
    workspaceId: workspaceId || '',
    type: NoteTypeEnum.Task,
    deleted: false,
    ...externalFilter, // Merge status, search, dueDate filters
  }), [workspaceId, externalFilter]);

  // Fetch tasks when the *value* of the query variables changes.
  //
  // Keying the effect on a serialized snapshot instead of object identity is
  // deliberate: callers legitimately build `filter`/`sort` inline, and one
  // unmemoized literal would otherwise refetch on every render forever. The
  // ref carries the live variables in so they are not effect dependencies.
  const variables = { filter, sort, first: pageSize };
  const variablesRef = useRef(variables);
  variablesRef.current = variables;
  const variablesKey = JSON.stringify(variables);

  useEffect(() => {
    if (!isLovablePreview() && workspaceId && !skip) {
      previousCountRef.current = 0;
      getTasks({ variables: variablesRef.current });
    }
  }, [workspaceId, variablesKey, getTasks, skip]);

  // Update hasMore based on pageInfo
  useEffect(() => {
    if (data?.notes?.pageInfo) {
      setHasMore(data.notes.pageInfo.hasNextPage);
    }
  }, [data?.notes?.pageInfo]);

  // Transform backend data to match UI expectations
  const tasks = useMemo(() => {
    return (data?.notes?.edges.map(edge => edge.node) || []) as Note[];
  }, [data?.notes?.edges]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return Promise.resolve();

    return fetchMore({
      variables: { after: data?.notes?.pageInfo.endCursor },
    }).then(() => {
      // Update previous count for tracking
      previousCountRef.current = tasks.length;
    });
  }, [hasMore, loading, fetchMore, data, tasks.length]);

  // In Lovable Preview mode, return dummy data
  if (isLovablePreview()) {
    // Apply client-side filtering for preview mode
    let filteredTasks = [...dummyTasks];

    if (externalFilter.status) {
      const statusMap: Record<string, boolean> = {
        [NoteStatusEnum.Todo]: false,
        [NoteStatusEnum.Completed]: true,
      };
      const completed = statusMap[externalFilter.status];
      filteredTasks = filteredTasks.filter(t => t.completed === completed);
    }

    // Client-side sorting for preview mode
    if (sort && sort.length > 0) {
      const sortField = sort[0].field;
      const sortDir = sort[0].sort;

      filteredTasks.sort((a, b) => {
        let aVal: number | string, bVal: number | string;

        if (sortField.includes('priority')) {
          const priorityOrder = { High: 2, Medium: 1, Low: 0 };
          aVal = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
          bVal = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
        } else if (sortField.includes('due')) {
          aVal = a.dueDate ? new Date(a.dueDate).getTime() : 0;
          bVal = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        } else if (sortField.includes('title')) {
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
        } else {
          aVal = a.id;
          bVal = b.id;
        }

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDir === 'ASC' ? aVal - bVal : bVal - aVal;
        }

        const cmp = String(aVal).localeCompare(String(bVal));
        return sortDir === 'ASC' ? cmp : -cmp;
      });
    }

    return {
      tasks: filteredTasks as unknown as Note[],
      loading: false,
      hasMore: false,
      loadMore: () => Promise.resolve(),
      refetch: () => Promise.resolve({ data: { notes: { edges: [] } } }),
      error: undefined,
    };
  }

  return {
    tasks,
    loading,
    hasMore,
    loadMore,
    refetch,
    error
  };
};
